import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import client from '../api/client';

const TAGLINES = [
  'Better Data. Better Models.',
  'Limit Algorithm Bias.',
  'Deliver Quality Models.',
];

const SERVICES = [
  {
    num: '01',
    text: 'Supervised Fine-Tuning',
    desc: 'High-quality instruction-response datasets for domain adaptation and model specialization.',
    shape: 'image',
  },
  {
    num: '02',
    text: 'RLHF & Preference Data',
    desc: 'Human preference rankings for reward model training and alignment.',
    shape: 'text',
  },
  {
    num: '03',
    text: 'Multimodal Data Pipelines',
    desc: 'Structured annotation for text, image, audio, and documents.',
    shape: 'audio',
  },
  {
    num: '04',
    text: 'Bias & Safety Audits',
    desc: 'Identify bias before deployment.',
    shape: 'custom',
  },
];

const HOW_STEPS = [
  { label: 'Define the Task', desc: 'Share your model goals, domain, and data requirements.' },
  { label: 'Build the Pipeline', desc: 'We structure and execute your data workflow.' },
  { label: 'Human Validation', desc: 'Every dataset is quality-checked for consistency and accuracy.' },
  { label: 'Delivery', desc: 'Built for immediate integration into your AI workflow.' },
];

const BUDGET_OPTIONS = ['<$10k', '$10k–$20k', '$20k–$30k', '$30k+', '$100k+'];

function initServiceCanvas(canvas, shapeType) {
  if (!canvas) return;
  const SIZE = 110;
  const renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true });
  renderer.setSize(SIZE, SIZE);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(50, 1, 0.1, 100);
  camera.position.z = 3;

  let geo;
  if (shapeType === 'image') geo = new THREE.BoxGeometry(1.2, 1.2, 0.18);
  else if (shapeType === 'text') geo = new THREE.CylinderGeometry(0.55, 0.55, 1.4, 18);
  else if (shapeType === 'audio') geo = new THREE.TorusGeometry(0.65, 0.22, 12, 36);
  else geo = new THREE.IcosahedronGeometry(0.85, 0);

  const mat = new THREE.MeshBasicMaterial({ color: 0xb8d4f0, wireframe: true, transparent: true, opacity: 0.72 });
  const mesh = new THREE.Mesh(geo, mat);
  scene.add(mesh);

  let raf;
  function animate() {
    raf = requestAnimationFrame(animate);
    mesh.rotation.x += 0.005;
    mesh.rotation.y += 0.009;
    renderer.render(scene, camera);
  }
  animate();

  return () => { cancelAnimationFrame(raf); renderer.dispose(); };
}

export default function Home() {
  const heroCanvasRef = useRef(null);
  const svcRef0 = useRef(null);
  const svcRef1 = useRef(null);
  const svcRef2 = useRef(null);
  const svcRef3 = useRef(null);
  const [taglineIdx, setTaglineIdx] = useState(0);
  const [displayed, setDisplayed] = useState('');
  const [typing, setTyping] = useState(true);
  const [form, setForm] = useState({
    name: '',
    email: '',
    user_type: 'student',
    budget: '<$10k',
    description: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  /* --- typing animation --- */
  useEffect(() => {
    const target = TAGLINES[taglineIdx];
    let timer;
    if (typing) {
      if (displayed.length < target.length) {
        timer = setTimeout(() => setDisplayed(target.slice(0, displayed.length + 1)), 55);
      } else {
        timer = setTimeout(() => setTyping(false), 2200);
      }
    } else {
      if (displayed.length > 0) {
        timer = setTimeout(() => setDisplayed(displayed.slice(0, -1)), 25);
      } else {
        setTaglineIdx((i) => (i + 1) % TAGLINES.length);
        setTyping(true);
      }
    }
    return () => clearTimeout(timer);
  }, [displayed, typing, taglineIdx]);

  /* --- Hero Three.js: connected-node sphere --- */
  useEffect(() => {
    const canvas = heroCanvasRef.current;
    if (!canvas) return;
    const W = canvas.clientWidth || 520;
    const H = canvas.clientHeight || 520;

    const renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true });
    renderer.setSize(W, H);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(55, W / H, 0.1, 100);
    camera.position.z = 3.2;

    const COUNT = 90;
    const pts = [];

    for (let i = 0; i < COUNT; i++) {
      const phi = Math.acos(-1 + (2 * i) / COUNT);
      const theta = Math.sqrt(COUNT * Math.PI) * phi;
      const r = 1 + (Math.random() - 0.5) * 0.3;
      pts.push(new THREE.Vector3(
        r * Math.sin(phi) * Math.cos(theta),
        r * Math.sin(phi) * Math.sin(theta),
        r * Math.cos(phi),
      ));
    }

    /* particles */
    const pGeo = new THREE.BufferGeometry();
    const pArr = new Float32Array(COUNT * 3);
    pts.forEach((p, i) => { pArr[i * 3] = p.x; pArr[i * 3 + 1] = p.y; pArr[i * 3 + 2] = p.z; });
    pGeo.setAttribute('position', new THREE.BufferAttribute(pArr, 3));
    const pMat = new THREE.PointsMaterial({ color: 0xb8d4f0, size: 0.045, transparent: true, opacity: 0.95 });
    const points = new THREE.Points(pGeo, pMat);
    scene.add(points);

    /* edges */
    const edgePts = [];
    const THRESH = 0.72;
    for (let i = 0; i < COUNT; i++) {
      for (let j = i + 1; j < COUNT; j++) {
        if (pts[i].distanceTo(pts[j]) < THRESH) {
          edgePts.push(pts[i].x, pts[i].y, pts[i].z, pts[j].x, pts[j].y, pts[j].z);
        }
      }
    }
    const eGeo = new THREE.BufferGeometry();
    eGeo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(edgePts), 3));
    const eMat = new THREE.LineBasicMaterial({ color: 0x8ab8d8, transparent: true, opacity: 0.22 });
    const edges = new THREE.LineSegments(eGeo, eMat);
    scene.add(edges);

    let t = 0;
    let raf;
    function animate() {
      raf = requestAnimationFrame(animate);
      t += 0.006;
      const s = 1 + 0.04 * Math.sin(t * 1.8);
      points.scale.setScalar(s);
      edges.scale.setScalar(s);
      points.rotation.y += 0.003;
      edges.rotation.y += 0.003;
      points.rotation.x = 0.15 * Math.sin(t * 0.4);
      edges.rotation.x = 0.15 * Math.sin(t * 0.4);
      renderer.render(scene, camera);
    }
    animate();

    return () => { cancelAnimationFrame(raf); renderer.dispose(); };
  }, []);

  /* --- Service card 3D illustrations --- */
  useEffect(() => {
    const shapes = ['image', 'text', 'audio', 'custom'];
    const refs = [svcRef0, svcRef1, svcRef2, svcRef3];
    const cleanups = refs.map((ref, i) => initServiceCanvas(ref.current, shapes[i]));
    return () => cleanups.forEach((c) => c && c());
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name.trim() || !form.description.trim()) {
      setFormError('Please fill in all fields.');
      return;
    }
    setSubmitting(true);
    setFormError('');
    try {
      await client.post('/api/demo-requests/', form);
      setSubmitted(true);
    } catch {
      setFormError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  function scrollTo(id) {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  }

  return (
    <div style={s.page}>
      {/* smooth scroll */}
      <style>{`
        html { scroll-behavior: smooth; }
        * { box-sizing: border-box; }

        .services-grid {
          grid-template-columns: repeat(4, minmax(0, 1fr));
        }

        @media (max-width: 980px) {
          .services-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
        }

        @media (max-width: 640px) {
          .services-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      {/* ── NAV ── */}
      <nav style={s.nav}>
        <span style={s.brand}>AINA</span>
        <div style={s.navLinks}>
          {[
            { label: 'What We Do', id: 'what-we-do' },
            { label: 'Services', id: 'services' },
            { label: 'How It Works', id: 'how-it-works' },
            { label: 'Request Demo', id: 'demo' },
          ].map(({ label, id }) => (
            <button key={id} style={s.navLink} onClick={() => scrollTo(id)}>{label}</button>
          ))}
        </div>
        <a href="/login" style={s.loginBtn}>Login</a>
      </nav>

      {/* ── HERO ── */}
      <section style={s.hero}>
        <div style={s.heroLeft}>
          <div style={s.taglineWrap}>
            <span style={s.tagline}>{displayed}</span>
            <span style={s.cursor}>|</span>
          </div>
          <p style={s.heroSub}>
            We help AI teams build high-quality datasets for supervised fine-tuning, preference learning, multimodal models, and safety evaluations
          </p>
          <button style={s.ctaBtn} onClick={() => scrollTo('demo')}>Request a Demo</button>
        </div>
        <div style={s.heroRight}>
          <canvas ref={heroCanvasRef} style={s.heroCanvas} width={520} height={520} />
        </div>
      </section>

      {/* ── WHAT WE DO ── */}
      <section id="what-we-do" style={s.section}>
        <div style={s.sectionInner}>
          <h2 style={s.sectionTitle}>What We Do</h2>
          <div style={s.sectionDivider} />
        
          <p style={{ ...s.sectionText, marginTop: '1rem' }}>
            From supervised fine-tuning and RLHF to evaluation and safety audits, we help researchers, startups, and enterprises improve model performance through structured, reliable data pipelines.
          </p>
        </div>
      </section>

      {/* ── SERVICES ── */}
      <section id="services" style={{ ...s.section, background: '#0a0a0a' }}>
        <div style={s.sectionInner}>
          <h2 style={s.sectionTitle}>Services</h2>
          <div style={s.sectionDivider} />
          <div className="services-grid" style={s.serviceGrid}>
            {SERVICES.map(({ num, text, desc }, idx) => (
              <div key={num} style={s.serviceCard}>
                <canvas
                  ref={[svcRef0, svcRef1, svcRef2, svcRef3][idx]}
                  style={s.serviceCanvas}
                  width={110}
                  height={110}
                />
                <span style={s.serviceNum}>{num}</span>
                <span style={s.serviceText}>{text}</span>
                <p style={s.serviceDesc}>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section id="how-it-works" style={s.section}>
        <div style={s.sectionInner}>
          <h2 style={s.sectionTitle}>How It Works</h2>
          <div style={s.sectionDivider} />
          <div style={s.stepsRow}>
            {HOW_STEPS.map((step, i) => (
              <React.Fragment key={i}>
                <div style={s.step}>
                  <div style={s.stepNum}>{i + 1}</div>
                  <div style={s.stepLabel}>{step.label}</div>
                  <div style={s.stepDesc}>{step.desc}</div>
                </div>
                {i < HOW_STEPS.length - 1 && <div style={s.stepArrow}>→</div>}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* ── DEMO FORM ── */}
      <section id="demo" style={{ ...s.section, background: '#0a0a0a' }}>
        <div style={{ ...s.sectionInner, maxWidth: '560px' }}>
          <h2 style={s.sectionTitle}>Request a Demo</h2>
          <div style={s.sectionDivider} />

          {submitted ? (
            <div style={s.successBox}>
              <p style={s.successText}>We'll be in touch soon.</p>
            </div>
          ) : (
            <form style={s.form} onSubmit={handleSubmit}>
              <div style={s.formGroup}>
                <label style={s.label}>Name</label>
                <input
                  style={s.input}
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                />
              </div>

              <div style={s.formGroup}>
                <label style={s.label}>Email</label>
                <input
                  style={s.input}
                  type="email"
                  required
                  placeholder="you@example.com"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                />
              </div>

              <div style={s.formGroup}>
                <label style={s.label}>Are you a:</label>
                <div style={s.radioRow}>
                  {['student', 'researcher', 'company'].map((t) => (
                    <label key={t} style={s.radioLabel}>
                      <input
                        type="radio"
                        name="user_type"
                        value={t}
                        checked={form.user_type === t}
                        onChange={() => setForm((f) => ({ ...f, user_type: t }))}
                        style={{ marginRight: '5px' }}
                      />
                      {t.charAt(0).toUpperCase() + t.slice(1)}
                    </label>
                  ))}
                </div>
              </div>

              <div style={s.formGroup}>
                <label style={s.label}>Budget</label>
                <select
                  style={s.select}
                  value={form.budget}
                  onChange={(e) => setForm((f) => ({ ...f, budget: e.target.value }))}
                >
                  {BUDGET_OPTIONS.map((b) => (
                    <option key={b} value={b}>{b}</option>
                  ))}
                </select>
              </div>

              <div style={s.formGroup}>
                <label style={s.label}>Project / Data Description</label>
                <textarea
                  style={s.textarea}
                  rows={4}
                  required
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                />
              </div>

              {formError && <p style={s.errorText}>{formError}</p>}

              <button style={s.submitBtn} type="submit" disabled={submitting}>
                {submitting ? 'Submitting…' : 'Submit'}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer style={s.footer}>
        <span>© {new Date().getFullYear()} AINA · Data Annotation Platform</span>
      </footer>
    </div>
  );
}

const s = {
  page: {
    minHeight: '100vh',
    background: '#000',
    fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
    color: '#f0f0f0',
    margin: 0,
    padding: 0,
  },

  /* nav */
  nav: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 2rem',
    height: '56px',
    background: '#000000',
    color: '#ffffff',
    position: 'sticky',
    top: 0,
    zIndex: 100,
    borderBottom: '1px solid #222',
  },
  brand: { fontWeight: 800, fontSize: '1.2rem', letterSpacing: '0.1em', color: '#fff' },
  navLinks: { display: 'flex', gap: '0.1rem' },
  navLink: {
    background: 'none',
    border: 'none',
    color: '#aaa',
    cursor: 'pointer',
    fontSize: '0.85rem',
    padding: '0.4rem 0.9rem',
    letterSpacing: '0.04em',
    transition: 'color 0.15s',
  },
  loginBtn: {
    color: '#fff',
    textDecoration: 'none',
    border: '1px solid #555',
    padding: '0.35rem 1rem',
    fontSize: '0.85rem',
    letterSpacing: '0.04em',
  },

  /* hero */
  hero: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: 'calc(100vh - 56px)',
    padding: '0 4rem',
    background: '#000',
    gap: '2rem',
  },
  heroLeft: { flex: '1', maxWidth: '520px' },
  taglineWrap: { marginBottom: '1.5rem', minHeight: '3.5rem', display: 'flex', alignItems: 'center' },
  tagline: {
    fontSize: 'clamp(1.8rem, 3.5vw, 2.8rem)',
    fontWeight: 800,
    color: '#ffffff',
    letterSpacing: '-0.01em',
    lineHeight: 1.15,
  },
  cursor: {
    fontSize: 'clamp(1.8rem, 3.5vw, 2.8rem)',
    fontWeight: 300,
    color: '#b8c5d1',
    animation: 'blink 1s step-start infinite',
    marginLeft: '2px',
  },
  heroSub: { color: '#aaa', fontSize: '1rem', lineHeight: 1.7, marginBottom: '2rem', maxWidth: '420px' },
  ctaBtn: {
    background: '#fff',
    color: '#000',
    border: 'none',
    padding: '0.85rem 2rem',
    fontSize: '0.9rem',
    fontWeight: 700,
    cursor: 'pointer',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
  },
  heroRight: { flex: '1', display: 'flex', justifyContent: 'center', alignItems: 'center' },
  heroCanvas: { width: '520px', height: '520px', maxWidth: '100%', display: 'block' },

  /* sections */
  section: { padding: '5rem 2rem', background: '#000' },
  sectionInner: { maxWidth: '960px', margin: '0 auto' },
  sectionTitle: { fontSize: '1.8rem', fontWeight: 800, margin: '0 0 0.5rem', letterSpacing: '-0.01em', color: '#f0f0f0' },
  sectionDivider: { width: '40px', height: '3px', background: '#f0f0f0', margin: '0 0 2rem' },
  sectionText: { fontSize: '1.05rem', lineHeight: 1.75, color: '#aaa', maxWidth: '680px' },

  /* services */
  serviceGrid: {
    display: 'grid',
    gap: '2.5rem 1.5rem',
    alignItems: 'start',
  },
  serviceCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    padding: '0.25rem 0',
    background: 'transparent',
    border: 'none',
  },
  serviceNum: { fontSize: '0.72rem', fontWeight: 700, color: '#b8c5d1', letterSpacing: '0.1em' },
  serviceText: { fontSize: '0.95rem', color: '#e0e0e0', lineHeight: 1.5 },
  serviceDesc: { fontSize: '0.88rem', color: '#9aa3ab', lineHeight: 1.6, margin: 0, maxWidth: '240px' },
  serviceCanvas: { width: '110px', height: '110px', display: 'block', marginBottom: '0.25rem' },

  /* how it works */
  stepsRow: { display: 'flex', alignItems: 'flex-start', gap: '0.5rem', flexWrap: 'wrap' },
  step: { flex: '1', minWidth: '130px', padding: '1.25rem', border: '1px solid #333', background: '#000' },
  stepNum: { fontSize: '1.6rem', fontWeight: 800, color: '#b8c5d1', lineHeight: 1 },
  stepLabel: { fontSize: '0.9rem', fontWeight: 700, color: '#f0f0f0', margin: '0.5rem 0 0.25rem' },
  stepDesc: { fontSize: '0.78rem', color: '#888', lineHeight: 1.5 },
  stepArrow: { fontSize: '1.4rem', color: '#ccc', paddingTop: '1.4rem', flexShrink: 0 },

  /* demo form */
  form: { display: 'flex', flexDirection: 'column', gap: '1.25rem' },
  formGroup: { display: 'flex', flexDirection: 'column', gap: '0.4rem' },
  label: { fontSize: '0.8rem', fontWeight: 700, color: '#aaa', letterSpacing: '0.07em', textTransform: 'uppercase' },
  input: {
    background: '#111',
    border: '1px solid #333',
    color: '#f0f0f0',
    padding: '0.7rem 0.9rem',
    fontSize: '0.92rem',
    outline: 'none',
    width: '100%',
  },
  radioRow: { display: 'flex', gap: '1.5rem' },
  radioLabel: { color: '#ccc', fontSize: '0.9rem', cursor: 'pointer', display: 'flex', alignItems: 'center' },
  select: {
    background: '#111',
    border: '1px solid #333',
    color: '#f0f0f0',
    padding: '0.7rem 0.9rem',
    fontSize: '0.92rem',
    outline: 'none',
    width: '100%',
  },
  textarea: {
    background: '#111',
    border: '1px solid #333',
    color: '#f0f0f0',
    padding: '0.7rem 0.9rem',
    fontSize: '0.92rem',
    resize: 'vertical',
    outline: 'none',
    width: '100%',
    fontFamily: 'inherit',
  },
  submitBtn: {
    background: '#fff',
    color: '#000',
    border: 'none',
    padding: '0.85rem 2rem',
    fontSize: '0.88rem',
    fontWeight: 700,
    cursor: 'pointer',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    alignSelf: 'flex-start',
  },
  errorText: { color: '#e07070', fontSize: '0.85rem', margin: 0 },
  successBox: { padding: '2rem 0' },
  successText: { color: '#b8c5d1', fontSize: '1.2rem', fontWeight: 600, margin: 0 },

  /* footer */
  footer: {
    background: '#000',
    color: '#555',
    padding: '1.5rem 2rem',
    fontSize: '0.8rem',
    letterSpacing: '0.04em',
    textAlign: 'center',
    borderTop: '1px solid #222',
  },
};
